<template>
    <div class="time-expense-form">
        <loading :active.sync="isLoaderVisible"></loading>
        <form class="form-horizontal" role="form" id="time-expense-create-form" @submit.prevent="validateBeforeSubmit" method="post" novalidate autocomplete="off">
            <div v-if="errorMessages.length > 0" class="row">
                <div class="alert alert-danger col-sm-offset-2 col-sm-8" role="alert">
                    <ul>
                        <li v-for="e in errorMessages">
                            {{ e }}
                        </li>
                    </ul>
                </div>
            </div>
            <!-- KII-1402: Add TE entry options to allow direct code selection or building one from action, target, and role dropdowns -->
            <div v-if="!isMarketing && !$store.getters.restrictedEditMode" class="form-group">
                <label class="col-sm-2 control-label">T&E Entry Options:</label>
                <div class="col-sm-6">
                    <div class="radio">
                        <label v-if="!$store.getters.isEditForm" for="teEntryOptionBuildTime" class="radio-inline">
                            <input type="radio" name="teEntryOption" id="teEntryOptionBuildTime" value="BuildTime" v-model="selectedTeEntryOption">
                            Build Time
                        </label>
                        <label for="teEntryOptionSelectTime" class="radio-inline">
                            <input type="radio" name="teEntryOption" id="teEntryOptionSelectTime" value="SelectTime" v-model="selectedTeEntryOption">
                            Select Time
                        </label>
                        <label v-if="!isOutlook" for="teEntryOptionExpense" class="radio-inline">
                            <input type="radio" name="teEntryOption" id="teEntryOptionExpense" value="Expense" v-model="selectedTeEntryOption">
                            Expense/Outside Services
                        </label>
                    </div>
                </div>
            </div>

            <div class="form-group" :class="{'has-error': errors.has('date') }">
                <label for="date" class="col-sm-2 control-label">Date:</label>
                <div class="col-sm-4 col-md-3 col-lg-2">
                    <div class="w-175px">
                        <datepicker v-validate.disable="'required'" name="date" v-model="timeEntry.date" :calendar-button="true" calendar-button-icon="fa fa-calendar"
                                    :clear-button="false" clear-button-icon="fa fa-times" :bootstrap-styling="true"
                                    format="yyyy MMM dd" :typeable="isTypeable"
                                    :disabled="isDatePickerDisabled"></datepicker>
                    </div>
                    <p class="text-danger" v-if="errors.has('date')">{{ errors.first('date') }}</p>
                </div>
            </div>

            <div class="form-group" :class="{'has-error': errors.has('username') }">
                <label for="username" class="col-sm-2 control-label">User:</label>

                <!-- Match activity-picker's right-side wrapper -->
                <div class="col-sm-10">
                    <div class="row">
                        <!-- Use the SAME inner col width as Activity's first dropdown -->
                        <!-- If Activity is col-sm-3 for BuildTime and col-sm-6 otherwise: -->
                        <div class="col-sm-6 col-md-5 col-lg-3">
                            <user-picker v-validate="'required'"
                                         name="username"
                                         v-model="timeEntry.username"
                                         :disabled="$store.getters.restrictedEditMode || timeEntry.isAuditEdit"
                                         :is-outlook="isOutlook">
                            </user-picker>
                        </div>
                    </div>

                    <p class="text-danger" v-if="errors.has('username')">{{ errors.first('username') }}</p>
                </div>
            </div>


            <template v-if="isUnbound">
                <unbound-inputs :is-outlook="isOutlook"></unbound-inputs>
            </template>

            <template v-if="isMarketing">
                <marketing-inputs @contactPickerClick="pickContact"></marketing-inputs>
            </template>

            <activity-picker ref="activitySelect" :is-marketing="isMarketing" :is-outlook="isOutlook" v-validate.disable="'required'" name="activity" v-bind:username="timeEntry.username"
                             v-model="timeEntry.activityCode" :te-entry-option="selectedTeEntryOption" class="w-100 select-compact" :activity-error="activityError">
            </activity-picker>

            <template v-if="$store.getters.selectedActivity">
                <div class="form-group">
                    <quantity-input ref="quantityInput" :activity="$store.getters.selectedActivity" :is-outlook="isOutlook"></quantity-input>

                    <template v-if="isBillableActivity">
                        <label class="col-sm-2 col-md-1 control-label">
                            Billable?
                        </label>
                        <div class="col-sm-2">
                            <div class="radio">
                                <label for="billableYes" class="radio-inline">
                                    <input type="radio" name="isBillable" id="billableYes" value="Yes" v-model="timeEntry.isBillable" :disabled="timeEntry.isAuditEdit">
                                    Yes
                                </label>
                                <label for="billableNo" class="radio-inline">
                                    <input type="radio" name="isBillable" id="billableNo" value="No" v-model="timeEntry.isBillable" :disabled="timeEntry.isAuditEdit">
                                    No
                                </label>
                            </div>
                        </div>
                    </template>
                </div>

                <template v-if="isAR">
                    <invoice-picker :is-outlook="isOutlook" ref="invoicePicker" :disabled="$store.getters.restrictedEditMode"></invoice-picker>
                </template>

                <template v-if="isTimeActivity">
                    <time-input></time-input>
                </template>

                <template v-if="$store.getters.isEmailActivity && !isOutlook">
                    <div class="form-group">
                        <label v-if="!$store.getters.isEditForm" class="col-sm-2 control-label">
                            Do you want KEA to send this email?
                        </label>
                        <label v-else class="col-sm-2 control-label">
                            Did KEA send this email?
                        </label>
                        <div class="col-sm-6">
                            <div v-if="!$store.getters.isEditForm" class="radio">
                                <label for="sendEmailYes" class="radio-inline">
                                    <input type="radio" name="sendEmail" id="sendEmailYes" value="Yes" v-model="timeEntry.sendEmail" @change="changeEmailSend" :disabled="$store.getters.selectedActivity.code === $acConstants('EMAIL_REPORT')">
                                    Yes
                                </label>
                                <label for="sendEmailNo" class="radio-inline">
                                    <input type="radio" name="sendEmail" id="sendEmailNo" value="No" v-model="timeEntry.sendEmail" @change="changeEmailSend" :disabled="$store.getters.selectedActivity.code === $acConstants('EMAIL_REPORT')">
                                    No, the email has already been sent
                                </label>
                            </div>
                            <p v-else-if="timeEntry.sendEmail === 'Yes'">
                                Yes
                            </p>
                            <p v-else-if="timeEntry.sendEmail === 'No'">
                                No
                            </p>
                            <p v-else>
                                Sent from Outlook
                            </p>
                        </div>
                    </div>
                    <template v-if="!$store.getters.restrictedEditMode && !$store.getters.nonEmailEditMode">
                        <email-inputs @contactPickerClick="pickContact"
                                      :is-accounts-receivable="isAR"
                                      :email-from="emailFrom"
                                      :disabled="false">
                        </email-inputs>
                    </template>
                    <template v-else-if="!$store.getters.nonEmailEditMode">
                        <email-inputs @contactPickerClick="pickContact"
                                      :is-accounts-receivable="isAR"
                                      :email-from="emailFrom"
                                      :disabled="true">
                        </email-inputs>
                    </template>
                </template>

                <template v-if="($store.getters.selectedActivity && ($store.getters.selectedActivity.codeType !== 2 || (timeEntry.sendEmail === 'No' && timeEntry.relatedToTeId === null))) || isOutlook">
                    <comment-inputs :is-outlook="isOutlook"></comment-inputs>
                </template>

                <div class="form-group" :class="{'has-error': errors.has('reportCycle')}" v-if="$store.getters.selectedActivity.code === $acConstants('EMAIL_REPORT')">
                    <label for="reportCycle" class="col-sm-2 control-label">Next Report Due:</label>
                    <div class="col-sm-3 col-md-2">
                        <div class="input-group w-150px">
                            <input v-validate.disable="'required'" type="number" step="5" class="form-control"
                                   id="reportCycle" name="reportCycle" v-model="reportCycle"
                                   placeholder="Days till next report" min="5" :max="365"
                                   :disabled="$store.getters.restrictedEditMode || timeEntry.isAuditEdit" />
                            <span class="input-group-addon">days</span>
                        </div>
                        <p class="text-danger" v-if="errors.has('reportCycle')">{{ errors.first('reportCycle') }}</p>
                        <div v-if="reportCycle && reportCycle > 0">
                            <small class="text-muted">{{ nextReportDate }}</small>
                        </div>
                    </div>
                </div>

                <div class="form-group" v-if="!timeEntry.hasFollowup">
                    <div class="col-sm-offset-2 col-sm-8">
                        <button type="button" class="btn btn-link followup-btn" @click='addFollowup' :disabled="timeEntry.isAuditEdit">
                            <i class="fa fa-plus" aria-hidden="true"></i>
                            <span> Add Follow Up </span>
                        </button>
                    </div>
                </div>

                <div class="form-group" v-else>
                    <div class="col-sm-offset-2 col-sm-8">
                        <button type="button" class="btn btn-link followup-btn" @click='removeFollowup' :disabled="timeEntry.isAuditEdit">
                            <i class="fa fa-trash" aria-hidden="true"></i>
                            <span> Remove Follow Up </span>
                        </button>
                    </div>
                </div>

                <template v-if="timeEntry.hasFollowup">
                    <followup :is-outlook="isOutlook"></followup>
                </template>

            </template>

            <div class="form-group footer-buttons" style="margin-bottom: 135px">
                <div class="col-sm-offset-2 col-sm-8">
                    <div class="button-group">
                        <button type="submit" @click="submitAnother = isOutlook" :class="['btn', 'btn-primary', $store.getters.selectedActivity ? '' : 'disabled']">Save</button>
                        <button type="submit" v-if="!$store.getters.isEditForm && !isOutlook" @click="submitAnother = true" :class="['btn', $store.getters.selectedActivity ? 'btn-default' : 'btn-primary disabled']">Save and add another T&E entry</button>
                        <button v-if="!isOutlook" class="btn btn-link" @click.prevent="cancel()">Cancel</button>
                    </div>
                </div>
            </div>
        </form>
        <modal v-model="showCancelConfirm" title="Confirm Cancel" @hide="closeCancelModal" ref="modal" size="sm" ok-text="Yes" cancel-text="No">
            <p>By canceling, all form entries will be lost. Are you sure you want to cancel?</p>
        </modal>

        <modal v-model="showOpenFileConfirm" title="The file is closed" @hide="closeFileModal" ref="modal" size="sm">
            <p>This claim is currently closed. To add billable T&E entries, the claim must be re-opened. Click OK to re-open the claim or Cancel to abort.</p>
        </modal>

        <modal v-model="showIsWholeFoodsIncidentConfirm" title="Is this still an incident?" @hide="closeIncidentModal"
               ref="modal" size="md" ok-text='Change to "full handle"' cancel-text='Keep status as "incident"'>
                <h2>This claim is set up as an incident.</h2>
                <p>to change the status, click below:</p>
        </modal>
    </div>
</template>

<script>
    import Vue from "vue";
    import _ from "lodash";
    import Loading from 'vue-loading-overlay';
    import UserPicker from "./user-picker";
    import UnboundInputs from "./unbound-inputs";
    import MarketingInputs from "./marketing-inputs";
    import ActivityPicker from "./activity-picker";
    import QuantityInput from "./quantity-input";
    import TimeInput from "./time-input";
    import EmailInputs from "./email-inputs";
    import CommentInputs from "./comment-inputs";
    import InvoicePicker from "./invoice-picker";
    import Followup from "./followup";
    import "es6-promise/auto"; // ES6 Promises Polyfill for IE11 support
    import Datepicker from 'vuejs-datepicker'
    import { TimePicker, Modal, MessageBox } from 'uiv';
    import axios from 'axios';
    import { shortDateFormat, timeFormat } from '../../_shared/date-utils';
    import { TE_ENTRY_OPTIONS } from '../../_shared/constants';


    Vue.config.devtools = false;
    Vue.config.productionTip = false;

    Vue.component("user-picker", UserPicker);
    Vue.component("unbound-inputs", UnboundInputs);
    Vue.component("marketing-inputs", MarketingInputs);
    Vue.component("activity-picker", ActivityPicker);
    Vue.component("quantity-input", QuantityInput);
    Vue.component("time-input", TimeInput);
    Vue.component("email-inputs", EmailInputs);
    Vue.component("comment-inputs", CommentInputs);
    Vue.component("followup", Followup);
    Vue.component("datepicker", Datepicker);
    Vue.component("time-picker", TimePicker);
    Vue.component("invoice-picker", InvoicePicker);

    export default {
        data: function () {
            return {
                timeEntry: this.$store.state.timeEntry,
                selectedTeEntryOption: !this.$store.getters.isEditForm ? TE_ENTRY_OPTIONS.BUILDTIME : TE_ENTRY_OPTIONS.SELECTTIME,
                allUsers: [],
                submitAnother: false,
                showCancelConfirm: false,
                showOpenFileConfirm: false,
                showIsWholeFoodsIncidentConfirm: false,
                showIncidentConfirmAgain: true,
                errorMessages: [],
                isLoaderVisible: false,
                fileToBeAudited: this.$store.state.fileToBeAudited,
                activityError: null
            };
        },
        props: {
            isUnbound: Boolean,
            isMarketing: Boolean,
            isOutlook: Boolean,
            reload: Function
        },
        components: { Modal, MessageBox, Loading },
        computed: {
            isBillableActivity() {
                if (this.isMarketing) {
                    return false;
                }
                let selectedActivity = this.$store.getters.selectedActivity;
                return selectedActivity && selectedActivity.code !== 'CLS' && selectedActivity.code !== 'AR';
            },
            isAR() {
                let selectedActivity = this.$store.getters.selectedActivity;
                return selectedActivity && selectedActivity.code === 'AR';
            },
            isTimeActivity() {
                let selectedActivity = this.$store.getters.selectedActivity;
                return selectedActivity && selectedActivity.codeType === 1;
            },
            emailFrom() {
                let self = this;
                let user = _.find(this.allUsers, function (u) { return u.initials === self.timeEntry.username; });
                if (user && ((this.timeEntry.sendEmail == "Yes" && this.$store.getters.isEmailActivity) || this.$store.getters.restrictedEditMode)) {
                    return user.email;
                }
                return null;
            },
            isTypeable() {
                // there is a bug in Safari with the datepicker
                // https://stackoverflow.com/a/23522755/2616170
                !/^((?!chrome|android).)*safari/i.test(navigator.userAgent);
            },
            reportCycle: {
                get() {
                    return this.$store.state.reportCycle;
                },
                set(value) {
                    this.$store.state.reportCycle = value;
                }
            },
            nextReportDate() {
                if (!this.reportCycle || this.reportCycle <= 0) {
                    return '';
                }

                let futureDate;

                if (this.$store.getters.isEditForm) {
                    // When editing grab the originally enetred date from at_file
                    futureDate = new Date(this.$store.state.abeyanceDate);
                }
                else {
                    const today = new Date();
                    futureDate = new Date(today);
                    futureDate.setDate(today.getDate() + parseInt(this.reportCycle) - parseInt(this.$store.state.abeyanceOffset));
                }

                return shortDateFormat(futureDate, ' ');
            },
            isDatePickerDisabled() {
                return this.timeEntry.sendEmail == "Yes" && this.$store.getters.isEmailActivity;
            }
        },
        methods: {
            addFollowup() {
                this.timeEntry.hasFollowup = true;
                this.timeEntry.followupAdjuster = this.$store.state.claimAdjuster;
            },
            removeFollowup() {
                this.timeEntry.hasFollowup = false;
                this.timeEntry.actionItem = "";
                this.timeEntry.followupDate = null;
                this.timeEntry.followupAdjuster = "";
            },
            minorResetTimeEntryObject() {
                // reseting anything that depends on activity type
                this.timeEntry.hours = null;
                this.timeEntry.units = null;
                this.timeEntry.dollars = null;
                this.timeEntry.time = new Date();
                this.timeEntry.timezone = this.$store.state.userTimezone;
                this.timeEntry.emailTo = null;
                this.timeEntry.attachInvoice = "Yes";
                this.timeEntry.invoiceId = "";
                this.timeEntry.isMultiInvoice = false;
                this.timeEntry.isSameSubscriberInvoices = true;
                if (this.$refs.quantityInput !== undefined && this.timeEntry.fileId != null && this.timeEntry.fileId != 0 && this.timeEntry.activityCode) {
                    this.$refs.quantityInput.getCodeTimeLimit();
                }
                this.timeEntry.isNewTransportation = true;
                let note = this.timeEntry.noteText;
                if (!this.isOutlook) {
                    this.timeEntry.comment = null;
                }
                else if (note.indexOf("Overdue KA Invoice") === 0) {
                    // For outlook remove the overdue invoice phrase from content
                    this.timeEntry.noteText = note.replace(note.substring(0, note.indexOf("\n") + 1), "");
                }
                this.timeEntry.relatedToTeId = null;
                this.timeEntry.emailToCheckboxes = [];
            },
            majorResetTimeEntryObject() {
                // reseting everything except date and username
                this.minorResetTimeEntryObject()
                this.$refs.activitySelect.clearSelection();
                this.timeEntry.date = new Date();
                this.timeEntry.date.setHours(0, 0, 0, 0); // set the time to 12am
                this.timeEntry.id = null;
                this.timeEntry.fileId = this.isUnbound || this.isMarketing ? null : this.$store.state.fileId;
                this.timeEntry.insuredCompany = this.isUnbound || this.isMarketing ? null : this.$store.state.insuredCompany;
                this.timeEntry.comment = "";
                this.timeEntry.noteText = "";
                this.removeFollowup();
                this.timeEntry.isMarketingEvent = "No";
                this.timeEntry.marketingContactId = null;
                this.timeEntry.marketingLocationId = null;
                this.timeEntry.marketingContactLabel = null;
                this.$store.state.reviveSession = false;
                this.timeEntry.sendEmail = "No";
                this.$store.state.reportCycle = this.$store.state.fileReportCycle;
            },
            getPostData() {
                let te = this.timeEntry;
                // set the followup time to midnight
                if (te.followupDate) {
                    if (!(te.followupDate instanceof Date)) {
                        te.followupDate = new Date(te.followupDate);
                    }
                    te.followupDate.setHours(0, 0, 0, 0);
                }
                return {
                    id: te.id,
                    branchId: this.isMarketing ? null : te.branchId,
                    fileId: this.isMarketing ? null : te.fileId,
                    insuredCompany: te.insuredCompany,
                    date: shortDateFormat(te.date, "-"),
                    time: isNaN(te.time) ? "" : timeFormat(te.time),
                    timezone: te.timezone,
                    userName: te.username,
                    activityCode: te.activityCode,
                    hours: te.hours,
                    units: te.units,
                    dollars: te.dollars,
                    nonBillable: te.isBillable === 'Yes' ? false : true,
                    attachInvoice: te.sendEmail === 'Yes' && te.attachInvoice === 'Yes' ? true : false,
                    invoiceId: te.invoiceId === "No unpaid invoices found" ? null : te.invoiceId,
                    sendEmail: te.sendEmail === null || this.isOutlook ? null : (te.sendEmail === 'Yes' ? true : false),
                    emailTo: this.isOutlook ? this.$store.state.emailTo : te.emailTo,
                    emailFrom: this.isOutlook ? this.$store.state.emailFrom : this.emailFrom,
                    comment: te.comment,
                    hasFollowup: te.hasFollowup,
                    actionItem: te.actionItem,
                    followupDate: te.followupDate,
                    noteText: te.noteText,
                    followupAdjuster: te.followupAdjuster,
                    emailToCheckboxes: te.emailToCheckboxes,
                    isMarketing: this.isMarketing,
                    isMarketingEvent: !this.isMarketing ? false : (te.isMarketingEvent === 'Yes' ? true : false),
                    marketingLocationId: this.isMarketing ? te.marketingLocationId : null,
                    marketingContactId: this.isMarketing ? te.marketingContactId : null,
                    marketingContactLabel: te.marketingContactLabel,
                    isAuditEdit: te.isAuditEdit,
                    isOutlook: this.isOutlook,
                    relatedToTeId: te.relatedToTeId,
                    relatedARInvoiceList: null,
                    teEntryOption: this.selectedTeEntryOption,
                    activityCodeAction: this.$refs.activitySelect.selectedActivity !== null ? this.$refs.activitySelect.selectedActivity.value : null,
                    activityCodeTarget: this.$refs.activitySelect.selectedTarget !== null ? this.$refs.activitySelect.selectedTarget.value : null,
                    activityCodeRole: this.$refs.activitySelect.selectedRole !== null ? this.$refs.activitySelect.selectedRole.value : null,
                    claimNumber: this.$store.state.claimNumber,
                    reportCycle: this.reportCycle,
                    lastReportNumber: this.$store.state.lastReportNumber
                };
            },
            validateField(field) {
                this.$validator.validate(field);
            },
            validateBeforeSubmit(event) {
                this.errorMessages = [];

                this.$validator.validateAll().then((result) => {
                    if (result) {
                        if (this.$store.state.isClosed && !this.isAR && this.timeEntry.isBillable == 'Yes' && !this.timeEntry.isAuditEdit) {
                            this.showOpenFileConfirm = true;
                        } else if (this.$store.state.isWholeFoodsParentIncident && this.showIncidentConfirmAgain) {
                            this.showIsWholeFoodsIncidentConfirm = true;
                        } else {
                            // Check if invoice picker component is present and has multiple selections
                            if (this.isAR && this.$refs.invoicePicker && this.$refs.invoicePicker.selectedInvoiceIds && this.$refs.invoicePicker.selectedInvoiceIds.length > 0) {
                                // Use multi-save logic
                                this.saveMultipleEntries();
                            } else {
                                // Use original single-save logic
                                this.saveSingleEntry();
                            }
                        }
                    }
                    console.log(this.errors);
                });
            },
            async saveMultipleEntries() {
                let self = this;
                try {
                    this.isLoaderVisible = true;

                    // Get the selected invoice IDs from the invoice picker component
                    const selectedInvoiceIds = this.$refs.invoicePicker.selectedInvoiceIds;
                    const noInvoicesMessage = this.$refs.invoicePicker.noInvoicesMessage;

                    // Filter out the "no invoices" message
                    const validInvoiceIds = selectedInvoiceIds.filter(invoiceId => invoiceId !== noInvoicesMessage);

                    if (validInvoiceIds.length === 0) {
                        throw new Error('No valid invoices selected');
                    }

                    let successCount = 0;
                    let failCount = 0;
                    let lastError = null;
                    let firstTeId = null;

                    // Process each invoice sequentially, stop if any errors occur to prevent partial saves 
                    try {
                        let invoiceId = null;
                        for (let i = 0; i < validInvoiceIds.length; i++) {
                            invoiceId = validInvoiceIds[i];
                            console.log("invoiceId: ", invoiceId);
                            // Create post data for this specific invoice
                            const postData = Object.assign({}, this.getPostData(), { invoiceId: invoiceId });
                            // Only first entry should have the hours, rest should be 0
                            if (i > 0) {
                                postData.hours = 0;
                                postData.sendEmail = this.isOutlook ? null : false;
                                postData.relatedToTeId = firstTeId;
                            }
                            else {
                                // Pass all selected invoices so they can be attched in email sent only through the first selection
                                postData.relatedARInvoiceList = validInvoiceIds;
                            }

                            // Set up URL and auth headers
                            var postUrl = "/core/api/";
                            var authHeader = "";
                            if (this.isOutlook) {
                                postUrl = this.$store.state.keaUrl + postUrl + "timeexpenses";
                                authHeader = { headers: { "Authorization": "Bearer " + this.$store.state.accessToken } };
                            } else {
                                postUrl += "timeexpenses";
                            }
                            // Make the API call
                            const response = await axios.post(postUrl, postData, authHeader);
                            // Check if session has timed-out
                            if (response.request.responseURL && response.request.responseURL.toLowerCase().includes("/authentication/login")) {
                                throw new Error("Session Expired");
                            }
                            // If sending email, save the TE id to relate to the following TEs to prevent editing of the content
                            if (i == 0 && postData.sendEmail) {
                                firstTeId = response.data.id;
                            }
                            successCount++;
                        }
                    } catch (error) {
                        failCount++;
                        lastError = error;
                        console.error(`Failed to save entry for invoice ${invoiceId}:`, error);
                    }
                    this.isLoaderVisible = false;

                    // Handle results
                    if (failCount === 0) {
                        // All entries saved successfully
                        let successMsg = "";
                        if (self.timeEntry.id) {
                            successMsg = `${successCount} entries successfully updated`;
                            self.$store.state.lastEditId = self.$store.state.editId;
                            self.$store.state.editId = 0;
                            self.$props.reload();
                        } else {
                            let selectedActivity = self.$store.getters.selectedActivity;
                            if (self.isMarketing) {
                                successMsg = `A T&E for ${selectedActivity.description} (${selectedActivity.code}) has been successfully added`;
                            } else {
                                successMsg = (selectedActivity.code === self.$acConstants('OUT_KILOMETRES_CODE') || selectedActivity.code === self.$acConstants('OUT_TRAVEL_TIME_CODE')) ?
                                    `T&E codes ${self.$acConstants('OUT_TRAVEL_TIME_CODE')} and ${self.$acConstants('OUT_KILOMETRES_CODE')} for travel have been successfully added to KA claim# ${self.$store.state.kaClaimNumber}` :
                                    `${successCount} T&E entries for ${selectedActivity.description} (${selectedActivity.code}) have been successfully added to KA claim# ${self.$store.state.kaClaimNumber}`;
                            }
                        }
                        vm.$snotify.success(successMsg, {
                            timeout: 3500,
                            showProgressBar: false,
                            closeOnClick: false,
                            pauseOnHover: true
                        });
                        // Handle post-save navigation
                        this.handlePostSaveNavigation();
                    } else {
                        // All entries failed
                        throw lastError || new Error('Failed to save');
                    }

                } catch (error) {
                    this.isLoaderVisible = false;
                    this.showIncidentConfirmAgain = true;

                    if (error.message === "Session Expired") {
                        vm.$snotify.error("Session Expired. Changes were not saved", {
                            timeout: 3500,
                            showProgressBar: false,
                            closeOnClick: false,
                            pauseOnHover: true
                        });
                    } else if (error.response && error.response.status === 400) {
                        let validationErrors = error.response.data;
                        for (var key in validationErrors) {
                            if (!self.isUnbound && (key === "fileId" || key === "branchId" || key === "errorMessage")) {
                                self.errorMessages.push(validationErrors[key]);
                            }
                            self.errors.add({ field: key, msg: validationErrors[key] });
                        }
                    } else {
                        let msg = error.response ? error.response.statusText : error.message;
                        vm.$snotify.error(msg, {
                            timeout: 3500,
                            showProgressBar: false,
                            closeOnClick: false,
                            pauseOnHover: true
                        });
                    }
                    console.log(error);
                }
            },
            saveSingleEntry() {
                let self = this;
                self.activityError = null;
                var postUrl = "/core/api/";
                var authHeader = "";
                if (this.isOutlook) {
                    postUrl = this.$store.state.keaUrl + postUrl + "timeexpenses";
                    authHeader = { headers: { "Authorization": "Bearer " + this.$store.state.accessToken } };
                } else {
                    postUrl += "timeexpenses";
                }

                const postData = this.getPostData();
                this.isLoaderVisible = true
                axios.post(postUrl, postData, authHeader)
                    .then(function (response) {
                        // Check if session has timed-out (detect the login page in the response).
                        if (response.request.responseURL && response.request.responseURL.toLowerCase().includes("/authentication/login")) {
                            vm.$snotify.error("Session Expired. Changes were not saved", {
                                timeout: 3500,
                                showProgressBar: false,
                                closeOnClick: false,
                                pauseOnHover: true
                            });
                        }

                        self.isLoaderVisible = false;
                        console.log(response);
                        let successMsg = "";
                        if (self.timeEntry.id) {
                            successMsg = "T&E successfully updated";
                            self.$store.state.lastEditId = self.$store.state.editId;
                            self.$store.state.editId = 0;
                            self.$props.reload();
                        } else {
                            let selectedActivity = self.$store.getters.selectedActivity;
                            if (self.isMarketing) {
                                successMsg = `A T&E for ${selectedActivity.description} (${selectedActivity.code}) has been successfully added`;
                            } else {
                                successMsg = (selectedActivity.code === self.$acConstants('OUT_KILOMETRES_CODE') || selectedActivity.code === self.$acConstants('OUT_TRAVEL_TIME_CODE')) ?
                                    `T&E codes ${self.$acConstants('OUT_TRAVEL_TIME_CODE')} and ${self.$acConstants('OUT_KILOMETRES_CODE')} for travel have been successfully added to KA claim# ${self.$store.state.kaClaimNumber}` :
                                    `A T&E for ${selectedActivity.description} (${selectedActivity.code}) has been successfully added to KA claim# ${self.$store.state.kaClaimNumber}`;
                                // For EMR, incrementing last report number in case user stays on screen which won't get it from DB
                                if (self.timeEntry.activityCode === self.$acConstants('EMAIL_REPORT')) {
                                    self.$store.state.lastReportNumber += 1;
                                }
                            }
                        }
                        vm.$snotify.success(successMsg, {
                            timeout: 3500,
                            showProgressBar: false,
                            closeOnClick: false,
                            pauseOnHover: true
                        });
                        // Handle post-save navigation
                        self.handlePostSaveNavigation();
                    })
                    .catch(function (error) {
                        self.isLoaderVisible = false;
                        self.showIncidentConfirmAgain = true;
                        if (error.response.status === 400) {
                            let validationErrors = error.response.data;

                            if (validationErrors.activity) {
                                self.activityError = validationErrors.activity;
                            }

                            for (var key in validationErrors) {
                                if (!self.isUnbound && (key === "fileId" || key === "branchId" || key === "errorMessage")) {
                                    self.errorMessages.push(validationErrors[key]);
                                }
                                self.errors.add({ field: key, msg: validationErrors[key] });
                            }
                        } else {
                            let msg = error.response.statusText;
                            vm.$snotify.error(msg, {
                                timeout: 3500,
                                showProgressBar: false,
                                closeOnClick: false,
                                pauseOnHover: true
                            });
                        }
                        console.log(error);
                    });
            },
            // Helper method to handle post-save navigation (extracted common logic)
            handlePostSaveNavigation() {
                let self = this;
                // remember the last 'who' selection and make it default for the next T&E entry for the file
                self.$store.state.defaultUser = self.timeEntry.username;

                // update missing FC message
                if (!self.isFC && self.$store.state.originalActivityCode === self.$acConstants('FIRST_CONTACT')) {
                    $("span.fc-msg").text("First Contact Missing");
                }
                else if (self.isFC) {
                    $("span.fc-msg").text("");
                }

                if (self.submitAnother) {
                    self.$store.state.isBillingReview = false; // exit billing review mode if this feautre is used
                    self.majorResetTimeEntryObject();
                } else {

                    if (self.$store.state.isBillingReview) {
                        var anchor = shortDateFormat(self.timeEntry.date, "-");
                        location.href = `/core/billing/${self.$store.state.branchId}/${self.$store.state.fileId}/unbilled#${anchor}`;
                    }

                    if (self.isMarketing) {
                        location.href = `/core`;
                    }

                    if (self.isUnbound && !self.isOutlook) {
                        self.$store.state.branchId = self.timeEntry.branchId;
                        self.$store.state.fileId = self.timeEntry.fileId;
                        self.$store.state.insuredCompany = self.timeEntry.insuredCompany;
                        location.href = `/core/timeexpense/${self.timeEntry.branchId}/${self.timeEntry.fileId}`;
                    }

                    // reset to page 1 of the file notes
                    self.$store.state.startPage = 1;

                    // go to the file notes
                    if (!self.isOutlook) {
                        vm.$router.push("/");
                    }
                    else {
                        self.majorResetTimeEntryObject();
                        vm.$router.push("/outlook");
                    }
                }
            },
            cancel() {
                this.showCancelConfirm = true;

            },
            closeCancelModal(msg) {
                if (msg === 'ok') {

                    if (this.$store.state.isBillingReview) {
                        location.href = `/core/billing/${this.$store.state.branchId}/${this.$store.state.fileId}/unbilled`;
                    }

                    if (!this.$store.getters.isEditForm) {
                        if (!this.isUnbound && !this.isMarketing) {
                            this.$router.push("/");
                        } else {
                            location.href = "/core";
                        }
                    } else {
                        this.$store.state.lastEditId = this.$store.state.editId;
                        this.$store.state.editId = 0;
                    }
                }
            },
            closeFileModal(msg) {
                let self = this;
                let errorMsg = "";
                var authHeader = "";
                if (msg === 'ok') {
                    let url = `/core/api/claims/${this.timeEntry.branchId}/${this.timeEntry.fileId}/reopen`
                    if (this.isOutlook) {
                        url = this.$store.state.keaUrl + url;
                        authHeader = { headers: { "Authorization": "Bearer " + this.$store.state.accessToken } };
                    }
                    axios.post(url, this.getPostData(), authHeader)
                        .then(function (response) {
                            // Check if session has timed-out (detect the login page in the response).
                            if (response.request.responseURL && response.request.responseURL.toLowerCase().includes("/authentication/login")) {
                                vm.$snotify.error("Session Expired. Changes were not saved", {
                                    timeout: 3500,
                                    showProgressBar: false,
                                    closeOnClick: false,
                                    pauseOnHover: true
                                });
                            }
                            self.$store.state.isClosed = false;
                            if ($("h3.closed-reopen").length) {
                                $("h3.closed-reopen").html("REOPENED");
                                $("h3.closed-reopen-mobile").html("Status: <span>REOPENED</status>");
                            }
                            self.validateBeforeSubmit();
                        })
                        .catch(function (error) {
                            loader.hide();
                            if (error.response.status === 404) {
                                errorMsg = "Claim does not exist.";
                            }
                            else if (error.response.status === 403) {
                                errorMsg = "Claim access is denied.";
                            }
                            else {
                                errorMsg = error.response.statusText
                            }
                            vm.$snotify.error(errorMsg, {
                                timeout: 3500,
                                showProgressBar: false,
                                closeOnClick: false,
                                pauseOnHover: true
                            });
                        });
                }
            },
            closeIncidentModal(msg) {
                let self = this;
                let errorMsg = "";
                if (msg === 'ok') {
                    let url = `/core/api/claims/${this.timeEntry.branchId}/${this.timeEntry.fileId}/unset-incident`
                    axios.post(url)
                        .then(function (response) {
                            // Check if session has timed-out (detect the login page in the response).
                            if (response.request.responseURL && response.request.responseURL.toLowerCase().includes("/authentication/login")) {
                                vm.$snotify.error("Session Expired. Changes were not saved", {
                                    timeout: 3500,
                                    showProgressBar: false,
                                    closeOnClick: false,
                                    pauseOnHover: true
                                });
                            }
                            self.$store.state.isWholeFoodsParentIncident = false;
                            MessageBox.alert({
                                html: true,
                                size: "md",
                                title: 'Status changed!',
                                content: `<h2>${self.$store.state.kaClaimNumber} is no longer an incident</h2><p>Don't forget to change the "Type of Fee" for the claim.</p>`
                            }, (msg) => {
                                // callback after modal dismissed
                                self.validateBeforeSubmit();
                            })

                        })
                        .catch(function (error) {
                            loader.hide();
                            if (error.response.status === 404) {
                                errorMsg = "Claim does not exist.";
                            }
                            else if (error.response.status === 403) {
                                errorMsg = "Claim access is denied.";
                            }
                            else {
                                errorMsg = error.response.statusText
                            }
                            vm.$snotify.error(errorMsg, {
                                timeout: 3500,
                                showProgressBar: false,
                                closeOnClick: false,
                                pauseOnHover: true
                            });
                        });
                }
                else {
                    // setting showIncidentConfirmAgain so calling validate does not pop the modal again
                    this.showIncidentConfirmAgain = false;
                    this.validateBeforeSubmit();
                }
            },
            pickContact(url) {
                this.isLoaderVisible = true;
                axios.post("/core/api/timeexpenses/temp", this.getPostData())
                    .then(function (response) {
                        location.href = url;
                    });
            },
            changeEmailSend() {
                if (this.isAR) {
                    this.$refs.invoicePicker.changeInvoice();
                }
            },
            isFC() {
                let selectedActivity = this.$store.getters.selectedActivity;
                return selectedActivity && selectedActivity.code === this.$acConstants('FIRST_CONTACT');
            },
        },
        beforeDestroy() {
            // Reset all the values in TimeEntry object in store. This is to get around the form showing
            // previous TimeEntry object data when navigating via route between log and create page
            this.timeEntry.userName = "";
            this.majorResetTimeEntryObject();
        },
        watch: {
            // watch activityCode change to decide isBillable and minor reset timeEntry object
            'timeEntry.activityCode': function (newVal, oldVal) {
                if (newVal === this.$acConstants('EMAIL_REPORT')) {
                    this.timeEntry.sendEmail = 'Yes';
                    this.timeEntry.attachInvoice = 'No';
                }
                if (oldVal !== newVal && (this.isMarketing || !this.$store.state.reviveSession)) {
                    if (this.isBillableActivity) {
                        this.timeEntry.isBillable = 'Yes';
                    }
                    else {
                        this.timeEntry.isBillable = 'No';
                    }

                    if (oldVal && oldVal.length) {
                        this.minorResetTimeEntryObject();
                    }

                    // focus the next input (by default the save button will be focussed
                    // because the form was hidden when you pressed tab)
                    window.setTimeout(function () {
                        let el = document.getElementById("quantity");
                        if (el !== null) {
                            el.focus();
                        }
                    }, 100);
                }
            },
            // reset attachInvoice and emailTo when email is not being sent
            'timeEntry.sendEmail': function (newVal, oldVal) {
                if (newVal === 'No') {
                    this.timeEntry.attachInvoice = 'No';
                    this.timeEntry.emailTo = null;
                }
                if (newVal === 'Yes' && this.timeEntry.activityCode !== this.$acConstants('EMAIL_REPORT')) {
                    this.timeEntry.attachInvoice = 'Yes';
                }
                if (newVal === "Yes") {
                    this.timeEntry.date = new Date();
                    this.timeEntry.date.setHours(0, 0, 0, 0); // set the time to 12am
                }
            },
            // React to the *transition* into the disabled state
            isDatePickerDisabled(newVal, oldVal) {
                if (newVal && !oldVal) {
                    this.timeEntry.date = new Date();
                    this.timeEntry.date.setHours(0, 0, 0, 0); // set the time to 12am
                }
            }
        },
        created() {
            let self = this;
            this.timeEntry.username = this.$store.state.defaultUser;
            this.timeEntry.timezone = this.$store.state.userTimezone;

            if (this.$store.state.newEntryDate !== undefined && this.$store.state.isBillingReview) {
                this.timeEntry.date = this.$store.state.newEntryDate;
                this.$store.state.newEntryDate = undefined;
            }

            if (this.isUnbound) {
                this.timeEntry.branchId = this.$store.state.currentUserBranchId;
            }
            else {
                this.timeEntry.branchId = this.$store.state.branchId;
                this.timeEntry.fileId = this.$store.state.fileId;
                this.timeEntry.insuredCompany = this.$store.state.insuredCompany;
            }

            var usersUrl = "/core/api/users";
            var authHeader = "";
            let promises = [];
            this.isLoaderVisible = true;
            if (this.isMarketing) {
                promises.push(axios.get("/core/api/activitycodes/marketing"));
            } else if (this.isOutlook) {
                this.timeEntry.comment = this.$store.state.comment;
                this.timeEntry.noteText = this.$store.state.noteText;
                authHeader = { headers: { "Authorization": "Bearer " + this.$store.state.accessToken } };
                promises.push(axios.get(this.$store.state.keaUrl + "/core/api/activitycodes/claims", authHeader));
                usersUrl = this.$store.state.keaUrl + "/core/api/users"
            }
            else {
                promises.push(axios.get("/core/api/activitycodes/claims"));
            }
            promises.push(axios.get(usersUrl, authHeader));
            axios.all(promises).then(function (results) {
                self.$store.state.activityCodes = results[0].data;
                self.allUsers = results[1].data;
                self.isLoaderVisible = false;
            }).catch(function (error) {
                self.isLoaderVisible = false;
                console.log(error.response);
            });

            if (this.$store.state.reviveSession) {
                // Restore this page after leaving to add/edit contact which is on a different URL
                axios.get("/core/api/timeexpenses/temp")
                    .then((response) => {
                        let entry = response.data;
                        self.timeEntry.id = entry.id;
                        self.$store.state.defaultUser = entry.userName;
                        self.timeEntry.username = entry.userName;
                        self.$store.state.branchId = entry.branchId;
                        self.timeEntry.branchId = entry.branchId;
                        self.$store.state.fileId = entry.fileId;
                        self.timeEntry.fileId = entry.fileId;
                        self.$store.state.insuredCompany = entry.insuredCompany;
                        self.timeEntry.insuredCompany = entry.insuredCompany;
                        self.timeEntry.date = new Date(entry.date);
                        this.selectedTeEntryOption = entry.teEntryOption;
                        this.$refs.activitySelect.setSelection(entry.activityCode, entry.activityCodeAction, entry.activityCodeTarget, entry.activityCodeRole);
                        self.timeEntry.time = Date.parse(entry.time);
                        self.timeEntry.isBillable = entry.nonBillable ? 'No' : 'Yes';
                        self.timeEntry.attachInvoice = entry.attachInvoice ? 'Yes' : 'No';
                        self.timeEntry.sendEmail = entry.sendEmail ? 'Yes' : 'No';
                        self.timeEntry.comment = entry.comment;
                        self.timeEntry.noteText = entry.noteText;
                        self.timeEntry.timezone = entry.timezone;
                        self.timeEntry.hours = entry.hours;
                        self.timeEntry.units = entry.units;
                        self.timeEntry.dollars = entry.dollars;
                        self.timeEntry.invoiceId = entry.invoiceId;
                        self.timeEntry.isMultiInvoice = entry.isMultiInvoice;
                        self.timeEntry.isSameSubscriberInvoices = entry.isSameSubscriberInvoices;
                        self.timeEntry.emailTo = entry.emailTo;
                        self.timeEntry.hasFollowup = entry.hasFollowup;
                        self.timeEntry.actionItem = entry.actionItem;
                        self.timeEntry.followupDate = entry.followupDate;
                        self.timeEntry.followupAdjuster = entry.followupAdjuster;
                        self.timeEntry.emailToCheckboxes = entry.emailToCheckboxes;
                        self.timeEntry.isMarketingEvent = entry.isMarketingEvent ? 'Yes' : 'No';
                        self.timeEntry.marketingContactId = entry.marketingContactId;
                        self.timeEntry.marketingLocationId = entry.marketingLocationId;
                        self.timeEntry.marketingContactLabel = entry.marketingContactLabel;
                        self.timeEntry.relatedToTeId = entry.relatedToTeId;
                        this.$store.state.reportCycle = entry.reportCycle;
                    });
            }
        }
    };
</script>

<style lang="scss">
    .time-picker .btn {
        margin-top: 0px;
    }
    .followup-btn {
        padding: 0px;
    }
    .followup-btn i.fa
    {
        margin-right: 6px;
    }
    #time-expense-create-form {
        min-height: 450px;
    }
    #time-expense-create-form .radio-inline:first-child {
        margin-left: 0px;
    }
    #time-expense-create-form .radio-inline {
        padding-top: 0px;
    }

    .vdp-datepicker__calendar {
        min-height: 255px;
        width: 255px;

        .cell {
            height: 28px;
            line-height: 28px;
        }
    }

    @media (max-width: 767px) {
        .margin-bottom-xs {
            margin-bottom: 15px;
        }
    }
</style>
